import React, { useEffect, useState } from "react";
import AdminLayout from "../../layout/AdminLayout";
import { useParams, useNavigate } from "react-router-dom";
import { getEntertainmentVideoById } from "../../../api/entertainmentVideo.api";
import { baseURL } from "../../../api/api";
import { Link } from "react-router-dom";
import {
  FiPlus,
  FiCalendar,
  FiMapPin,
  FiType,
  FiMonitor,
  FiTruck,
  FiCheckCircle,
  FiClock,
  FiXCircle,
  FiUser,
} from "react-icons/fi";
import { format, parseISO } from "date-fns";

function EntertainmentVideoDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entertainmentVideoDetails, setEntertainmentVideoDetails] =
    useState(null);
  const [activeTab, setActiveTab] = useState("details");

  useEffect(() => {
    getEntertainmentVideoById(id)
      .then((res) => {
        setEntertainmentVideoDetails(res?.data);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return format(parseISO(dateString), "PPpp");
  };

  const handleScreenClick = (screenId) => {
    navigate(`/screendetails/${screenId}`);
  };

  const StatusBadge = ({ status }) => {
    switch (status) {
      case "approved":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <FiCheckCircle className="mr-1" /> Approved
          </span>
        );
      case "pending":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
            <FiClock className="mr-1" /> Pending
          </span>
        );
      case "rejected":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            <FiXCircle className="mr-1" /> Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
            Unknown
          </span>
        );
    }
  };

  return (
    <AdminLayout title="Infotainment Video Details">
      <div className="bg-white shadow rounded-lg mb-6">
        {/* Header with update button */}
        <div className="px-6 py-5 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-semibold text-gray-800">
            {entertainmentVideoDetails?.name || "Video Details"}
          </h2>
          <Link
            to={`/infotainmentvideos/edit/${id}`}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-black bg-yellow-400 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
          >
            <FiPlus className="mr-2" />
            Update Infotainment Video
          </Link>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => setActiveTab("details")}
              className={`whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm ${
                activeTab === "details"
                  ? "border-yellow-500 text-yellow-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Video Details
            </button>
            <button
              onClick={() => setActiveTab("screens")}
              className={`whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm ${
                activeTab === "screens"
                  ? "border-yellow-500 text-yellow-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Assigned Screens (
              {entertainmentVideoDetails?.screens?.length || 0})
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          {activeTab === "details" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Video Preview */}
              <div className="lg:col-span-1 lg:pl-4">
                {" "}
                {/* Added left padding for spacing */}
                <div className="bg-gray-100 rounded-lg overflow-hidden flex flex-col h-full">
                  <div className="flex-1">
                    {" "}
                    {/* This will allow the video container to grow */}
                    <video
                      src={`${baseURL}/s3/images/${entertainmentVideoDetails?.video?.name}`}
                      className="w-full h-full object-contain"
                      controls
                      style={{ minHeight: "200px" }} // Minimum height for better visibility
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Video Information
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center text-sm text-gray-600">
                        <FiType className="mr-2 text-gray-500" />
                        <span>
                          Type: {entertainmentVideoDetails?.video?.type}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Video Details */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-lg">
                  <div className="space-y-6">
                    {/* Basic Info */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">
                        Basic Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-start">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-yellow-50 flex items-center justify-center">
                            <FiType className="h-5 w-5 text-yellow-600" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-500">
                              Name
                            </p>
                            <p className="text-sm text-gray-900">
                              {entertainmentVideoDetails?.name}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-yellow-50 flex items-center justify-center">
                            <FiMapPin className="h-5 w-5 text-yellow-600" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-500">
                              Location
                            </p>
                            <p className="text-sm text-gray-900">
                              {entertainmentVideoDetails?.location}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-yellow-50 flex items-center justify-center">
                            <FiTruck className="h-5 w-5 text-yellow-600" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-500">
                              Type
                            </p>
                            <p className="text-sm text-gray-900 capitalize">
                              {entertainmentVideoDetails?.type}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-yellow-50 flex items-center justify-center">
                            <FiMonitor className="h-5 w-5 text-yellow-600" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-500">
                              Category
                            </p>
                            <p className="text-sm text-gray-900 capitalize">
                              {entertainmentVideoDetails?.category}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Schedule */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">
                        Schedule
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-start">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-yellow-50 flex items-center justify-center">
                            <FiCalendar className="h-5 w-5 text-yellow-600" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-500">
                              Start Date
                            </p>
                            <p className="text-sm text-gray-900">
                              {formatDate(entertainmentVideoDetails?.startDate)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-yellow-50 flex items-center justify-center">
                            <FiCalendar className="h-5 w-5 text-yellow-600" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-500">
                              End Date
                            </p>
                            <p className="text-sm text-gray-900">
                              {formatDate(entertainmentVideoDetails?.endDate)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Status & Creator */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">
                        Status & Creator
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-start">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-yellow-50 flex items-center justify-center">
                            <FiCheckCircle className="h-5 w-5 text-yellow-600" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-500">
                              Status
                            </p>
                            <div className="mt-1">
                              <StatusBadge
                                status={entertainmentVideoDetails?.status}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-yellow-50 flex items-center justify-center">
                            <FiUser className="h-5 w-5 text-yellow-600" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-500">
                              Created By
                            </p>
                            <p className="text-sm text-gray-900">
                              {entertainmentVideoDetails?.createdBy?.firstName}{" "}
                              {entertainmentVideoDetails?.createdBy?.lastName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {entertainmentVideoDetails?.createdBy?.email}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "screens" && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Assigned Screens
              </h3>
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Screen Name
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Type
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Online Status
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Last Connection
                      </th>
                      <th
                        scope="col"
                        className="relative py-3.5 pl-3 pr-4 sm:pr-6"
                      >
                        <span className="sr-only">Details</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {entertainmentVideoDetails?.screens?.map((screen) => (
                      <tr key={screen._id} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 capitalize">
                          {screen.model}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 capitalize">
                          {screen.screenType === "restaurant"
                            ? "Fixed Location"
                            : screen.screenType === "vehicle"
                            ? "Mobile Location"
                            : screen.screenType}
                        </td>

                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              screen.status === "Active"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {screen.status}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              screen.onlineStatus === "online"
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {screen.onlineStatus}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {screen.lastConnection
                            ? formatDate(screen.lastConnection)
                            : "Never"}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <button
                            onClick={() => handleScreenClick(screen._id)}
                            className="px-4 py-2 text-sm text-black bg-yellow-400 rounded-md hover:bg-yellow-500 transition duration-200"
                          >
                            Screen Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

export default EntertainmentVideoDetails;
